import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { FileDocument } from '../models/file-document.model';
import { AuthService } from '../auth/auth.service';
import { FileDocumentQueryService } from '../services/file-document-query.service';
import {MsgType, MsgTypeLabels, QueryDocument} from '../models/query-document.model';
import {MessageService} from "../errorHandler/message.service";
import {LocalStorageService} from "../services/local-storage.service";
import { TranslocoService } from '@ngneat/transloco';
import { DocQueryConfigService } from '../services/doc-query-config.service';

@Component({
  standalone: false,
  selector: 'app-file-document-query',
  templateUrl: './file-document-query.component.html',
  styleUrls: ['./_fileDocumentQuery.scss'],
})
export class FileDocumentQueryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() doc: FileDocument;
  @Input() organizationalUnitId: number;
  @Input() showQuery = false;
  @Input() hideCloseQuery = false;

  @Output() queryChanged = new EventEmitter<boolean>();
  @Output() activeChange = new EventEmitter<boolean>();
  @Output() closedQuery = new EventEmitter<boolean>();

  private _scrollContainer: ElementRef;

  @ViewChild('scrollContainer') set scrollContainer(content: ElementRef) {
    if (content) {
      this._scrollContainer = content;
      this.scrollToBottom();
    }
  }

  loading = false;
  querying = false; // Controla si el panel está abierto o solo se ve el botón
  hasQuery = false; // Controla si el documento ya tiene una consulta previa
  useDocComments = false;
  isMinimized = false;

  queryHistory: QueryDocument[] = [];
  currentUser: { id: number; lastName: string, firstName: string };

  queryForm = {
    messageType: null as string | null,
    message: ''
  };

  replyText = '';
  // Motivos configurados por la empresa en Configuración > Consultas
  motives: string[] = [];

  constructor(
    private readonly queryService: FileDocumentQueryService,
    private readonly authService: AuthService,
    private readonly msjService: MessageService,
    private readonly localStorage: LocalStorageService,
    private readonly docQueryConfigService: DocQueryConfigService,
    private readonly transloco: TranslocoService
  ) { }

  get docIsSigned(): boolean {
    return !!this.doc?.wasSignedByEmployee() && !this.hasQuery && !this.doc?.hasMessage;
  }


  ngOnInit() {
    const userId = this.authService.getUserId();
    this.useDocComments = this.localStorage.get('useDocComments');
    this.currentUser = {
      id: Number(userId),
      lastName: localStorage.getItem('userLastname'),
      firstName: localStorage.getItem('userFirstname'),
    };

    this.getMotives();

    if (this.doc?.id) {
      this.checkExistingQuery();
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['organizationalUnitId'] && !changes['organizationalUnitId'].firstChange) {
      this.getMotives();
    }

    if (changes['doc'] && this.doc) {
      this.checkExistingQuery();
    }

    if (changes['showQuery'] && !changes['showQuery'].currentValue) {
      this.closeQuery();
    }
  }

  // --- MÉTODOS DE FLUJO ---

  startQueryFlow() {
    if (!this.showQuery) return;
    this.querying = true;
    this.activeChange.emit(true);

    if (this.hasQuery) {
      this.scrollToBottom();
    }
  }
  closeQuery() {
    this.querying = false;
    this.isMinimized = false;
    this.activeChange.emit(false);
  }

  ngOnDestroy() {
    // eslint-disable-next-line no-console
    console.log('Destroy Query Component');
    this.closeQuery();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateInput(event: any) {
    const input = event.target as HTMLTextAreaElement;
    const regex = /[^a-zA-Z0-9ñÑáéíóúÁÉÍÓÚüÜ ¡!¿?().,;:\n]/g;

    if (regex.test(input.value)) {
      const cleanValue = input.value.replace(regex, '');
      input.value = cleanValue;
      this.replyText = cleanValue;
    }
  }

  toggleMinimize() {
    this.isMinimized = !this.isMinimized;
  }
  // --- LÓGICA DE DATOS ---

  private getMotives() {
    if (!this.organizationalUnitId) {
      this.motives = [];
      return;
    }

    this.docQueryConfigService.getSettings(this.organizationalUnitId).subscribe({
      next: (settings) => {
        this.motives = settings?.motives ?? [];
        if (!this.queryForm.messageType || !this.motives.includes(this.queryForm.messageType)) {
          this.queryForm.messageType = this.motives[0] ?? null;
        }
      },
      error: (err) => {
        this.motives = [];
        console.error("Error obteniendo los motivos de consulta", err);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFriendlyLabel(type: any): string {
    if (!type) return '';
    const numericType: number = MsgType[type] as unknown as number;
    const labelKey = MsgTypeLabels[numericType];
    // Los motivos configurables ya vienen como texto: se muestran tal cual
    return labelKey ? this.transloco.translate(labelKey) : type;
  }

  private checkExistingQuery() {
    this.loading = true;
    this.queryHistory = [];
    this.replyText = '';
    this.queryForm = { messageType: this.motives[0] ?? null, message: '' };

    this.queryService.getQueryData(this.doc.id, 4).subscribe({
      next: (data) => {
        this.queryHistory = data;
        this.hasQuery = data && data.length > 0;
        this.loading = false;
        this.scrollToBottom();
      },
      error: (err) => {
        this.loading = false;
        this.hasQuery = false;
        console.error("Error verificando consultas previas", err);
      }
    });
  }

  /** El motivo solo es obligatorio si la empresa tiene motivos configurados. */
  get canSubmitNewQuery(): boolean {
    const hasMotive = this.motives.length === 0 || !!this.queryForm.messageType;
    return hasMotive && !!this.queryForm.message?.trim();
  }

  async submitNewQuery() {
    if (!this.queryForm.messageType || !this.queryForm.message.trim() || !this.canSubmitNewQuery) {
      this.msjService.showInfo(this.transloco.translate('fileDocuments.fileDocumentQuery.completeAllFieldsMsg'), false, true);
      return;
    }

    this.loading = true;

    const newQuery: QueryDocument = {
      documentId: this.doc.id,
      documentationName: this.doc.metadatas.find(m => m.systemName === "_nomDocumentacion")?.metadataValue,
      ouId: this.organizationalUnitId,
      message: this.queryForm.message,
      messageType: this.queryForm.messageType,
      userId: this.currentUser.id
    };

    this.queryService.postQuery(newQuery).subscribe({
      next: (res) => {
        this.doc.hasMessage = true;
        this.doc.threadClosed = false;
        this.hasQuery = true;
        this.queryChanged.emit(true);
        this.queryHistory.push(res);
        this.loading = false;
        this.msjService.showInfo(this.transloco.translate('fileDocuments.fileDocumentQuery.querySentMsg'), false, true);
      },
      error: (_err) => {
        this.loading = false;
        this.msjService.showError(this.transloco.translate('fileDocuments.fileDocumentQuery.querySendErrorMsg'));
      }
    });
  }

  async sendReply() {
    if (!this.replyText.trim() || this.loading) return;

    this.loading = true;

    const reply: QueryDocument = {
      documentId: this.doc.id,
      documentationName: this.doc.metadatas.find(m => m.systemName === "_nomDocumentacion")?.metadataValue,
      ouId: this.organizationalUnitId,
      message: this.replyText,
      messageType: this.queryHistory[0]?.messageType || MsgType.Otras_consultas_recibo,
      userId: this.currentUser.id
    };

    this.queryService.postReply(reply).subscribe({
      next: (res) => {
        this.queryHistory.push(res);
        this.replyText = '';
        this.loading = false
        this.msjService.showInfo(this.transloco.translate('fileDocuments.fileDocumentQuery.querySentMsg'), false, true);
        this.scrollToBottom();
      },
      error: (err) => {
        this.loading = false;
        this.replyText = '';
        this.msjService.showError(err);
      }
    });
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this._scrollContainer?.nativeElement) {
        const el = this._scrollContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 300);
  }

  deleteMessage(msgId: string) {
    this.loading = true;
    this.queryService.deleteMessage(msgId).subscribe({
      next: () => {
        const msgIndex = this.queryHistory.findIndex(m => m.id === msgId);
        if(msgIndex !== -1){
          Object.assign(this.queryHistory[msgIndex], {
            isDeleted: true
          });
          this.queryHistory = [...this.queryHistory];
        }
        this.loading = false;
        this.msjService.showInfo(this.transloco.translate('fileDocuments.fileDocumentQuery.messageDeletedMsg'), false, true);
      },
      error: () => {
        this.loading = false;
        this.msjService.showError(this.transloco.translate('fileDocuments.fileDocumentQuery.messageDeleteErrorMsg'));
      }
    });
  }

  async closeQueryThread() {
    if (!this.queryHistory || this.queryHistory.length === 0) return;
    const docID = this.doc.id;

    this.loading = true;
    this.queryService.closeQuery(docID).subscribe({
      next: () => {
        this.loading = false;
        this.doc.threadClosed = true;
        this.closedQuery.emit(true);
        this.msjService.showInfo(this.transloco.translate('fileDocuments.fileDocumentQuery.queryClosedMsg'), false, true);
        this.closeQuery();
      },
      error: (err) => {
        this.loading = false;
        this.msjService.showError(this.transloco.translate('fileDocuments.fileDocumentQuery.queryCloseErrorMsg'));
        console.error("Error al cerrar hilo:", err);
      }
    });
  }
}
